import { useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useScrollReveal, revealClass } from '../hooks/useScrollReveal';

const portfolioItems = [
  {
    id: 1,
    titlePt: 'Cozinha IKEA Moderna',
    titleEs: 'Cocina IKEA Moderna',
    category: 'cozinhas',
    image: '/images/cozinha.webp',
  },
  {
    id: 2,
    titlePt: 'Piso Porcelanato',
    titleEs: 'Suelo de Porcelanato',
    category: 'pisos',
    image: '/images/piso.webp',
  },
  {
    id: 3,
    titlePt: 'Escritório Completo',
    titleEs: 'Oficina Completa',
    category: 'mobiliario',
    image: '/images/escritorio.webp',
  },
  {
    id: 4,
    titlePt: 'Renovação de Casa de Banho',
    titleEs: 'Renovación de Baño',
    category: 'remodelacoes',
    image: '/images/banheiro.webp',
  },
  {
    id: 5,
    titlePt: 'Trabalho de Pedreiro',
    titleEs: 'Trabajo de Albañilería',
    category: 'pedreiro',
    image: '/images/parede.webp',
  },
  {
    id: 6,
    titlePt: 'Cozinha Premium',
    titleEs: 'Cocina Premium',
    category: 'cozinhas',
    image: 'https://images.unsplash.com/photo-1592506119503-c0b18879bd5a?w=800&q=80',
  },
  {
    id: 7,
    titlePt: 'Piso Flutuante Premium',
    titleEs: 'Suelo Flotante Premium',
    category: 'pisos',
    image: 'https://images.unsplash.com/photo-1622936063167-e32e9f955438?w=800&q=80',
  },
  {
    id: 8,
    titlePt: 'Remodelação Interior',
    titleEs: 'Reforma Interior',
    category: 'remodelacoes',
    image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&q=80',
  },
];

const FILTERS = [
  { key: 'all', labelKey: 'filter_all' },
  { key: 'cozinhas', labelKey: 'filter_kitchens' },
  { key: 'pisos', labelKey: 'filter_floors' },
  { key: 'mobiliario', labelKey: 'filter_furniture' },
  { key: 'pedreiro', labelKey: 'filter_masonry' },
  { key: 'remodelacoes', labelKey: 'filter_renovations' },
];

export default function Portfolio() {
  const { t, lang } = useLang();
  const { ref, isVisible } = useScrollReveal();
  const [active, setActive] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const filtered =
    active === 'all'
      ? portfolioItems
      : portfolioItems.filter((item) => item.category === active);

  return (
    <section id="portfolio" className="py-24 sm:py-32 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={revealClass(isVisible)}>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#C2410C] mb-4">
            <span className="w-6 h-px bg-[#C2410C]" />
            {t('portfolio_label')}
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 tracking-tight mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="portfolio-title"
          >
            {t('portfolio_title')}
          </h2>
          <p className="text-base sm:text-lg text-zinc-500 max-w-2xl mb-10">
            {t('portfolio_subtitle')}
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActive(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                active === f.key
                  ? 'bg-[#C2410C] text-white shadow-sm'
                  : 'bg-white text-zinc-600 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-900'
              }`}
              data-testid={`portfolio-filter-${f.key}`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((item, i) => (
            <div
              key={item.id}
              className="group relative overflow-hidden rounded-xl cursor-pointer bg-zinc-100 aspect-square shadow-sm hover:shadow-xl transition-all duration-300"
              style={{
                animation: `fadeUp 0.5s ease-out ${i * 60}ms both`,
              }}
              onClick={() => setLightbox(item)}
              data-testid={`portfolio-item-${item.id}`}
            >
              <img
                src={item.image}
                alt={lang === 'pt' ? item.titlePt : item.titleEs}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <div className="w-full">
                  <p className="text-white font-semibold text-sm">
                    {lang === 'pt' ? item.titlePt : item.titleEs}
                  </p>
                </div>
                <ZoomIn className="text-white absolute top-4 right-4" size={20} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="portfolio-lightbox"
        >
          <button
            className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors p-2 rounded-full bg-white/10 hover:bg-white/20"
            onClick={() => setLightbox(null)}
            data-testid="lightbox-close"
          >
            <X size={24} />
          </button>
          <div
            className="max-w-4xl w-full rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.image}
              alt={lang === 'pt' ? lightbox.titlePt : lightbox.titleEs}
              className="w-full max-h-[85vh] object-contain bg-zinc-900"
            />
            <div className="bg-zinc-900 px-6 py-4">
              <p className="text-white font-semibold">
                {lang === 'pt' ? lightbox.titlePt : lightbox.titleEs}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
